import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const money = value => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const numberText = value => Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const FAQ = [
  ['What does feet per crew-day mean?', 'It is the amount of fence you expect the whole crew to install in one workday for this specific scope. Enter your own planning assumption rather than treating a generic production rate as a benchmark.'],
  ['Why does the planner show two labor-cost views?', 'Production-hour cost uses the calculated fractional crew-days. Full-day cost rounds installation to whole scheduled workdays. Seeing both helps you choose the costing approach that matches how you actually schedule and pay the crew.'],
  ['Does this include travel, setup or callbacks?', 'Only if you enter those hours in Additional direct labor hours. The planner does not automatically add travel, supervision, callbacks, warranty work or other overhead.'],
  ['Can I send the labor estimate into Profit Guard?', 'Yes. When you enter an hourly direct-labor cost, you can send either the production-hour labor cost or the full-day labor cost into Profit Guard.'],
];

export default function FenceLaborCrewPlanner() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const initialFeet = searchParams.get('feet') || '';
  const initialPrice = searchParams.get('price') || '';
  const sourceLabel = searchParams.get('job') || '';
  const [project, setProject] = useState('');
  const [feet, setFeet] = useState(initialFeet);
  const [crewSize, setCrewSize] = useState('');
  const [feetPerCrewDay, setFeetPerCrewDay] = useState('');
  const [hoursPerDay, setHoursPerDay] = useState('');
  const [hourlyLaborCost, setHourlyLaborCost] = useState('');
  const [extraLaborHours, setExtraLaborHours] = useState('0');
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => {
    const footage = Number(feet);
    const crew = Number(crewSize);
    const production = Number(feetPerCrewDay);
    const hoursDay = Number(hoursPerDay);
    const hourly = Number(hourlyLaborCost);
    const extraHours = Number(extraLaborHours);

    const coreValid = String(feet).trim() !== ''
      && String(crewSize).trim() !== ''
      && String(feetPerCrewDay).trim() !== ''
      && String(hoursPerDay).trim() !== ''
      && Number.isFinite(footage) && footage > 0
      && Number.isInteger(crew) && crew > 0
      && Number.isFinite(production) && production > 0
      && Number.isFinite(hoursDay) && hoursDay > 0
      && Number.isFinite(extraHours) && extraHours >= 0;

    const hourlyValid = String(hourlyLaborCost).trim() === '' || (Number.isFinite(hourly) && hourly >= 0);

    if (!coreValid || !hourlyValid) {
      return {
        valid:false, crewDays:0, scheduledDays:0, productionHours:0,
        fullDayHours:0, productionLaborCost:null, fullDayLaborCost:null,
      };
    }

    const crewDays = footage / production;
    const scheduledDays = Math.ceil(crewDays);
    const productionHours = crewDays * crew * hoursDay + extraHours;
    const fullDayHours = scheduledDays * crew * hoursDay + extraHours;
    const hasRate = String(hourlyLaborCost).trim() !== '';
    const productionLaborCost = hasRate ? productionHours * hourly : null;
    const fullDayLaborCost = hasRate ? fullDayHours * hourly : null;

    return {
      valid:true,
      crewDays,
      scheduledDays,
      productionHours,
      fullDayHours,
      productionLaborCost,
      fullDayLaborCost,
    };
  }, [feet, crewSize, feetPerCrewDay, hoursPerDay, hourlyLaborCost, extraLaborHours]);

  const planningText = useMemo(() => {
    if (!result.valid) return '';
    return [
      'PicketOps Fence Labor & Crew Plan',
      project.trim() ? 'Project: ' + project.trim() : null,
      'Fence footage: ' + numberText(feet) + ' ft',
      'Crew size: ' + Number(crewSize),
      'Production assumption: ' + numberText(feetPerCrewDay) + ' ft per crew-day',
      'Workday length: ' + numberText(hoursPerDay) + ' hours',
      'Additional direct labor hours: ' + numberText(extraLaborHours),
      '',
      'PLANNING OUTPUT',
      'Calculated production crew-days: ' + result.crewDays.toFixed(2),
      'Scheduled whole workdays: ' + result.scheduledDays,
      'Production-hour labor: ' + result.productionHours.toFixed(2) + ' worker-hours',
      'Full-day labor capacity: ' + result.fullDayHours.toFixed(2) + ' worker-hours',
      result.productionLaborCost === null ? null : 'Production-hour direct labor cost: ' + money(result.productionLaborCost),
      result.fullDayLaborCost === null ? null : 'Full-day direct labor cost: ' + money(result.fullDayLaborCost),
      '',
      'Planning estimate only. Results depend on the productivity, crew, workday and labor-cost assumptions entered here. Review access, terrain, material system, travel, supervision and job risk separately.',
    ].filter(Boolean).join('\n');
  }, [result, project, feet, crewSize, feetPerCrewDay, hoursPerDay, extraLaborHours]);

  const copyPlan = async () => {
    if (!planningText) return;
    try {
      await navigator.clipboard.writeText(planningText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const clear = () => {
    setProject('');
    setFeet(initialFeet);
    setCrewSize('');
    setFeetPerCrewDay('');
    setHoursPerDay('');
    setHourlyLaborCost('');
    setExtraLaborHours('0');
    setCopied(false);
  };

  const hasInput = [feet, crewSize, feetPerCrewDay, hoursPerDay, hourlyLaborCost].some(value => String(value).trim() !== '')
    || extraLaborHours !== '0';
  const workspaceTarget = isAuthenticated ? '/jobs' : '/contractor/signup';
  const priceParam = initialPrice ? '&price=' + encodeURIComponent(initialPrice) : '';
  const profitLink = laborCost => '/fence-profit-margin-calculator?labor=' + encodeURIComponent(laborCost.toFixed(2))
    + priceParam
    + '&job=' + encodeURIComponent(sourceLabel ? sourceLabel + ' · labor plan' : 'Fence labor planner');

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <header className="max-w-6xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span><span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span><span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span></span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link><span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link><span aria-hidden="true"> / </span>
          <span aria-current="page">Fence labor and crew planner</span>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-16 space-y-10">
        <section className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-md)">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free labor & crew planner · No signup required</p>
              <h1 className="mt-4 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">Turn fence footage into crew-days, labor hours and direct labor cost</h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">Use your own crew size and production assumption to plan install duration and compare fractional production hours with whole scheduled workdays.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild><a href="#labor-planner">Plan the labor <ApperIcon name="ArrowDown" size={16}/></a></Button>
                <Button variant="outline" asChild><Link to="/fence-profit-margin-calculator">Open Profit Guard</Link></Button>
              </div>
              {sourceLabel && <p className="mt-4 inline-flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm"><ApperIcon name="FileText" size={16} className="text-primary"/>Planning labor from {sourceLabel}</p>}
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-border bg-muted/30 p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Two views, no hidden assumption</p>
              <div className="mt-5 space-y-3">
                {[
                  ['Clock','Production-hour view','Uses the calculated fractional crew-days.'],
                  ['CalendarDays','Full-day view','Rounds installation to whole scheduled workdays.'],
                  ['Users','Crew capacity','Uses the crew size and workday length you enter.'],
                  ['CircleDollarSign','Direct labor cost','Appears only when you enter your hourly labor cost.'],
                ].map(([icon,title,detail])=><div key={title} className="flex gap-3 rounded-lg border border-border bg-background p-3"><span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><ApperIcon name={icon} size={16}/></span><div><p className="text-sm font-semibold">{title}</p><p className="mt-0.5 text-xs text-muted-foreground">{detail}</p></div></div>)}
              </div>
            </div>
          </div>
        </section>

        <section id="labor-planner" aria-labelledby="labor-heading" className="grid gap-5 scroll-mt-6 lg:grid-cols-[0.9fr_1.1fr]">
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-5">
              <div><p className="text-xs font-semibold uppercase tracking-wide text-primary">Crew assumptions</p><h2 id="labor-heading" className="mt-2 text-2xl font-heading font-semibold">Enter the productivity you actually plan around.</h2><p className="mt-2 text-sm text-muted-foreground">PicketOps does not supply a generic footage-per-day benchmark.</p></div>

              <div className="space-y-2"><Label htmlFor="labor-project">Project label <span className="text-muted-foreground">(optional)</span></Label><Input id="labor-project" value={project} onChange={event=>setProject(event.target.value)} placeholder="Smith privacy fence"/></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="labor-feet">Fence footage</Label><Input id="labor-feet" type="number" min="0.01" step="0.01" inputMode="decimal" value={feet} onChange={event=>setFeet(event.target.value)} placeholder="180"/></div>
                <div className="space-y-2"><Label htmlFor="labor-crew-size">Crew size</Label><Input id="labor-crew-size" type="number" min="1" step="1" inputMode="numeric" value={crewSize} onChange={event=>setCrewSize(event.target.value)} placeholder="3"/></div>
                <div className="space-y-2"><Label htmlFor="labor-production">Expected feet per crew-day</Label><Input id="labor-production" type="number" min="0.01" step="0.01" inputMode="decimal" value={feetPerCrewDay} onChange={event=>setFeetPerCrewDay(event.target.value)} placeholder="120"/></div>
                <div className="space-y-2"><Label htmlFor="labor-hours-day">Hours per workday</Label><Input id="labor-hours-day" type="number" min="0.01" step="0.25" inputMode="decimal" value={hoursPerDay} onChange={event=>setHoursPerDay(event.target.value)} placeholder="8"/></div>
                <div className="space-y-2"><Label htmlFor="labor-hourly">Direct labor cost / worker hour <span className="text-muted-foreground">(optional)</span></Label><Input id="labor-hourly" type="number" min="0" step="0.01" inputMode="decimal" value={hourlyLaborCost} onChange={event=>setHourlyLaborCost(event.target.value)} placeholder="32.50"/></div>
                <div className="space-y-2"><Label htmlFor="labor-extra">Additional direct labor hours</Label><Input id="labor-extra" type="number" min="0" step="0.25" inputMode="decimal" value={extraLaborHours} onChange={event=>setExtraLaborHours(event.target.value)} placeholder="0"/></div>
              </div>

              {hasInput && !result.valid && <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">Enter positive footage, whole-number crew size, positive feet per crew-day and positive workday hours. Optional hourly labor cost and additional hours must be nonnegative.</p>}
              <Button type="button" variant="ghost" onClick={clear}><ApperIcon name="RotateCcw" size={16}/>Clear planner</Button>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <section className="overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-sm)">
              <div className="border-b border-border bg-primary/5 p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Crew plan</p>
                <h2 className="mt-2 text-2xl font-heading font-semibold">{result.valid ? 'Schedule and labor capacity ready to review' : 'Complete the core crew assumptions'}</h2>
              </div>
              <dl className="grid grid-cols-2 gap-px bg-border sm:grid-cols-3">
                {[
                  ['Production crew-days',result.valid?result.crewDays.toFixed(2):'—','Fractional duration at entered productivity'],
                  ['Scheduled workdays',result.valid?result.scheduledDays:'—','Production rounded to whole workdays'],
                  ['Crew size',result.valid?Number(crewSize):'—','Workers in the entered crew'],
                  ['Production labor hours',result.valid?result.productionHours.toFixed(2):'—','Fractional production + extra hours'],
                  ['Full-day labor hours',result.valid?result.fullDayHours.toFixed(2):'—','Whole workdays + extra hours'],
                  ['Fence footage',result.valid?numberText(feet)+' ft':'—','Footage used in this plan'],
                ].map(([label,value,detail])=><div key={label} className="bg-card p-4 sm:p-5"><dt className="text-xs text-muted-foreground">{label}</dt><dd className="mt-1 text-2xl font-bold tabular-nums">{value}</dd><p className="mt-1 text-xs text-muted-foreground">{detail}</p></div>)}
              </dl>
            </section>

            {result.valid && result.productionLaborCost !== null && (
              <section className="grid gap-3 sm:grid-cols-2" aria-label="Direct labor cost views">
                <div className="rounded-xl border border-border bg-card p-5 shadow-(--shadow-sm)">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Production-hour direct labor</p>
                  <p className="mt-2 text-3xl font-bold tabular-nums">{money(result.productionLaborCost)}</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">Uses fractional production crew-days plus the additional labor hours entered.</p>
                  <Button variant="outline" className="mt-4 w-full" asChild><Link to={profitLink(result.productionLaborCost)}><ApperIcon name="ShieldCheck" size={16}/>Use in Profit Guard</Link></Button>
                </div>
                <div className="rounded-xl border border-primary/25 bg-primary/5 p-5 shadow-(--shadow-sm)">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary">Full-day direct labor</p>
                  <p className="mt-2 text-3xl font-bold tabular-nums">{money(result.fullDayLaborCost)}</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">Uses whole scheduled workdays plus the additional labor hours entered.</p>
                  <Button variant="outline" className="mt-4 w-full" asChild><Link to={profitLink(result.fullDayLaborCost)}><ApperIcon name="ShieldCheck" size={16}/>Use in Profit Guard</Link></Button>
                </div>
              </section>
            )}

            <p className="text-xs leading-relaxed text-muted-foreground">Planning tool only. Terrain, demolition, access, gate complexity, material system, weather, travel and crew experience can change real production. Use your own operating history when setting productivity assumptions.</p>
          </div>
        </section>

        <div className="flex flex-wrap gap-3">
          <Button type="button" onClick={copyPlan} disabled={!result.valid}><ApperIcon name={copied?'Check':'Copy'} size={16}/>{copied?'Copied':'Copy labor plan'}</Button>
          {result.valid?<Button variant="outline" asChild><a href={'data:text/plain;charset=utf-8,'+encodeURIComponent(planningText)} download="picketops-fence-labor-plan.txt"><ApperIcon name="Download" size={16}/>Download labor plan</a></Button>:<Button variant="outline" disabled><ApperIcon name="Download" size={16}/>Download labor plan</Button>}
          {result.valid&&<Button variant="outline" asChild><Link to={'/fence-install-schedule-planner?days='+encodeURIComponent(result.scheduledDays)+'&project='+encodeURIComponent(project.trim())}><ApperIcon name="CalendarDays" size={16}/>Build install schedule</Link></Button>}
          <Button variant="outline" asChild><Link to={workspaceTarget}>{isAuthenticated?'Open Jobs & scheduling':'Create account'} <ApperIcon name="ArrowRight" size={16}/></Link></Button>
        </div>

        <section aria-labelledby="labor-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="labor-faq-heading" className="text-2xl font-heading font-semibold">Fence labor planning questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">{FAQ.map(([question,answer])=><details key={question} className="py-4"><summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary><p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p></details>)}</div>
        </section>
      </main>
    </div>
  );
}

export const route={path:'/fence-labor-crew-planner',layout:'public'};
